import React, {
  useState,
  useRef,
  ReactElement,
  useEffect,
  useImperativeHandle,
  forwardRef,
  ComponentRef,
} from 'react'
import { SvgArrowTriangleDown } from '@chilipiper/icons/src/arrow/ArrowTriangleDown'
import noop from 'lodash/noop'
import { Field as FinalField } from 'react-final-form'
import { SvgCrossCircle, SvgMagnifyingGlass } from '@chilipiper/icons/src/design-system'
import { Box, Flex } from '../../new/core-components/box'
import { Checkbox } from '../checkbox'
import { Props as ButtonProps } from '../button/Button'

import { Icon } from '../../new/core-components/icon'
import { Popover, PopoverProps, PopoverTarget, PopoverTrigger } from '../popover'
import { Text } from '../../new/core-components/text'
import { SystemProps } from '../../new/theme'
import { ThemeColors } from '../../new/theme/colors'
import { TextField, Field } from '../text-field'
import { LinkButton, UnstyledButton } from '../button'
import { CursorManager } from './CursorManager'
import { useKeyPress } from './input-manager'
import { InputField } from './InputField'

import { OptionProps } from './Option'
import { Search, SearchListContainer } from './Search'
import { allOptionsContainerStyles, isSelectableOption, nodeContainsText } from './helpers'
import { useTypingWithinElement } from './hooks/useTypingWithinElement'
import { MappedOption } from './MappedOption'

type ModeType = 'ENABLE_TYPING' | 'SELECT_ONLY'
type LabelStyle = 'hide-on-select' | 'visible'

// Don't refactor old select, new select is done and will replace this one
/* eslint-disable complexity */
/* eslint-disable max-depth */

/** @deprecated - See {@link Select} */
export type SelectProps<ValueType = string> = Omit<React.InputHTMLAttributes<any>, 'onChange'> & {
  ariaLabel?: string
  arrowColor?: ThemeColors
  arrowDownHidden?: boolean
  bottomPopoverContent?: React.ReactNode
  children?: React.ReactElement<OptionProps>[] | React.ReactElement<OptionProps>
  customContentSection?: React.ReactElement<OptionProps>
  customPopoverTrigger?: React.ReactNode | React.FunctionComponent<{ focused: boolean }>
  customPopoverTriggerStyles?: ButtonProps
  disableFocusedStyles?: boolean
  error?: string
  extraOnOutsideClick?: () => void
  fieldStyle?: 'control' | 'embedded'
  hideSelectedOption?: boolean
  icon?: React.ComponentType
  id?: string
  initialState?: Partial<State>
  isFeSearch?: boolean
  isFillBgOnHover?: true
  // Please use MultiSelect component
  isMultiSelect?: boolean
  isPopoverFlipEnabled?: boolean
  isPortalForTarget?: boolean
  isWithClearAll?: boolean
  isWithSelectAll?: boolean
  label?: string
  labelPlacement?: 'inset' | 'outside'
  labelStyle?: LabelStyle
  labelStyles?: SystemProps
  mode?: ModeType
  offset?: PopoverProps['offset']
  onBlur?: () => void
  onChange: (value: ValueType) => void
  onFocus?: () => void
  onIsSelectAllChange?: (value: boolean) => void
  onRemoveIconClick?: React.MouseEventHandler<HTMLElement>
  optionsStyle?: { sameWidth: boolean }
  popoverBackground?: ThemeColors
  popoverTargetClassName?: string
  preventOverflowAltAxis?: boolean
  removeIconColor?: ThemeColors
  search?: (searchValue: string) => void
  searchPlaceholder?: string
  small?: boolean
  stayOpenOnSelect?: boolean
  value?: ValueType
  valueWhenAllOptionsAreCleared?: ValueType
  zIndex?: number
} & Omit<SystemProps, 'onChange' | 'cursor'>

export interface State {
  addCurrentItem?: () => void
  cursor: number
  hovered: number | undefined
  searchValue: string
  showCustomContentSection: boolean
  showing: boolean
}

const initialState = {
  showing: false,
  cursor: 0,
  hovered: undefined,
  searchValue: '',
  showCustomContentSection: false,
}

const SelectInner = <ValueType extends unknown = string>(
  props: SelectProps<ValueType>,
  ref: React.ForwardedRef<{
    changeVisibility: (visibility: boolean) => void
    setState: React.Dispatch<React.SetStateAction<State>>
  }>
) => {
  const {
    ariaLabel,
    arrowDownHidden,
    autoComplete,
    bottomPopoverContent,
    children,
    customPopoverTrigger,
    customPopoverTriggerStyles,
    disableFocusedStyles,
    extraOnOutsideClick = noop,
    hideSelectedOption,
    id,
    initialState: propsInitialState,
    isFeSearch,
    isFillBgOnHover,
    isMultiSelect,
    isPopoverFlipEnabled,
    isPortalForTarget,
    isWithClearAll,
    isWithSelectAll,
    label,
    labelPlacement = 'inset',
    labelStyle = 'visible',
    maxHeight,
    maxWidth,
    mode = 'SELECT_ONLY',
    offset,
    onBlur,
    onChange,
    onFocus,
    onIsSelectAllChange,
    onRemoveIconClick,
    optionsStyle = {
      sameWidth: true,
    },
    placeholder,
    popoverTargetClassName,
    preventOverflowAltAxis,
    removeIconColor,
    search,
    searchPlaceholder,
    stayOpenOnSelect,
    customContentSection,
    value,
    valueWhenAllOptionsAreCleared,
    zIndex = 1,
    ...others
  } = props
  const [state, setState] = useState<State>({ ...initialState, ...propsInitialState })
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const customTriggerRef = useRef<HTMLButtonElement>(null)

  const searchFieldRef = useRef<HTMLInputElement>(null)
  const downPress = useKeyPress('ArrowDown', containerRef.current)
  const upPress = useKeyPress('ArrowUp', containerRef.current)
  const escPress = useKeyPress('Escape', document.body)
  const isSearchEnabled = isFeSearch || search

  useEffect(() => {
    // reset the search when dropdown closed/opened
    if (isSearchEnabled) {
      if (search) {
        search('')
      }
      setState(s => ({ ...s, searchValue: '' }))
    }
  }, [state.showing, search, isSearchEnabled])

  useEffect(() => {
    setTimeout(() => {
      // can not use autoFocus because of scrollIntoView happening before popper mount
      if (searchFieldRef.current && state.showing) {
        searchFieldRef.current.focus()
      }
    }, 100)
  }, [state.showing])

  const optionsChildren = React.Children.toArray(children).map(child => {
    if (!React.isValidElement<OptionProps<ValueType>>(child)) {
      throw new Error('Invalid children')
    }

    return child.props
  })

  const optionsProps = optionsChildren.filter(isSelectableOption)

  const optionsValues = optionsProps.map(prop => prop.value)

  const changeVisibility = (visible: boolean) => {
    if (others.disabled) {
      return
    }
    if (visible) {
      inputRef.current?.focus()

      onFocus?.()
    } else {
      setTimeout(() => {
        customTriggerRef.current?.focus()
      }, 100)

      onBlur?.()
    }

    setState(s => ({
      ...s,
      showing: visible,
    }))
  }

  useImperativeHandle(ref, () => ({
    changeVisibility,
    setState,
  }))

  const selectItem = (index: number) => {
    if (value !== optionsValues[index]) {
      onChange(optionsValues[index] as unknown as ValueType)
    }
    if (!stayOpenOnSelect) {
      changeVisibility(false)
    }
  }

  const isOptionSelected = (optionValue: ValueType) =>
    isMultiSelect && value && (value as unknown as ValueType[]).includes(optionValue)

  const allChecked = optionsValues.every(isOptionSelected)
    ? true
    : optionsValues.some(isOptionSelected)
    ? '-'
    : false

  const handleSelectAllChange = () => {
    onIsSelectAllChange?.(!allChecked)
    const newValues = allChecked
      ? []
      : optionsProps.filter(prop => !prop.disabled).map(prop => prop.value)
    onChange(newValues as any)
  }

  const handleClearAllChange = () => {
    const newValue = valueWhenAllOptionsAreCleared ?? ([] as ValueType)
    onChange(newValue)
    changeVisibility(false)
  }

  const changeHoverState = (index?: number) => {
    setState(s => ({
      ...s,
      cursor: index === undefined ? -1 : index,
    }))
  }

  const findOptionByValue = (valueLocal?: ValueType) =>
    React.Children.toArray(children).find(child => {
      if (!React.isValidElement<OptionProps>(child)) {
        throw new Error('Invalid children')
      }

      const val = Array.isArray(valueLocal) ? valueLocal[0] : valueLocal

      return child.props.value === val
    })

  const findSelectedItem = () => findOptionByValue(value)

  const closeOnNavigate = () => {
    if (state.showing) {
      changeVisibility(false)
    }
  }

  const updateCursor = (index: number) => {
    setState(s => ({
      ...s,
      cursor: index,
    }))
  }

  const handleSearch = (value: string) => {
    setState(s => ({
      ...s,
      searchValue: value,
    }))
    if (search) {
      search(value)
    }
  }

  const selectedOption = findSelectedItem() as ReactElement<OptionProps>
  const selectedOptionContent = selectedOption?.props.renderAsSelected
    ? selectedOption.props.renderAsSelected()
    : selectedOption

  if (isMultiSelect && !Array.isArray(value)) {
    throw new Error(`Value should be an array when isMultiSelect is true`)
  }

  const timedTypedText = useTypingWithinElement(containerRef)
  const typedText = mode === 'ENABLE_TYPING' ? String(value) : timedTypedText

  useEffect(() => {
    const comparisonFunctionsInOrderOfPrecedence = ['startsWith', 'includes'] as const
    for (const comparisonFunction of comparisonFunctionsInOrderOfPrecedence) {
      const firstMatchIndex = React.Children.toArray(children).findIndex(option => {
        if (!React.isValidElement<OptionProps<ValueType>>(option)) {
          return false
        }
        return (
          isSelectableOption(option.props) &&
          nodeContainsText(option, typedText, comparisonFunction)
        )
      })
      if (firstMatchIndex !== -1) {
        changeHoverState(firstMatchIndex)
        return
      }
    }
  }, [children, typedText])

  const defaultSearchInputHeight = '48px'

  return (
    <Popover
      zIndex={zIndex}
      sameWidth={optionsStyle.sameWidth}
      offset={offset}
      placement='bottom-start'
      isFlipEnabled={isPopoverFlipEnabled}
      preventOverflowAltAxis={preventOverflowAltAxis}
    >
      <Box
        ref={containerRef}
        w='100%'
        onKeyPress={e => {
          if (e.key === 'Enter' && state.addCurrentItem) {
            state.addCurrentItem()
          }
        }}
      >
        <CursorManager<ValueType>
          items={optionsChildren}
          input={{
            downPress,
            upPress,
            escPress,
          }}
          onClose={closeOnNavigate}
          onCursorChange={updateCursor}
          cursor={state.cursor}
          active={state.showing}
        >
          <div style={{ width: '100%', cursor: 'pointer' }}>
            <PopoverTrigger w='100%' display='flex'>
              {(!selectedOptionContent || mode === 'ENABLE_TYPING') && !customPopoverTrigger && (
                <InputField
                  {...others}
                  mode={mode}
                  id={id}
                  ref={inputRef}
                  label={label}
                  labelPlacement={labelPlacement}
                  value={value}
                  placeholder={placeholder}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChange(e.target.value as unknown as ValueType)
                  }
                  onClick={() => {
                    changeVisibility(true)
                  }}
                  focused={disableFocusedStyles ? false : state.showing}
                  isFillBgOnHover={isFillBgOnHover}
                  arrowDownHidden={arrowDownHidden}
                />
              )}

              {customPopoverTrigger && (
                <UnstyledButton
                  ref={customTriggerRef}
                  id={id}
                  w='auto'
                  m={0}
                  p={0}
                  onClick={() => changeVisibility(!state.showing)}
                  {...customPopoverTriggerStyles}
                >
                  {customPopoverTrigger instanceof Function
                    ? customPopoverTrigger({ focused: state.showing })
                    : customPopoverTrigger}
                </UnstyledButton>
              )}

              {selectedOptionContent && !customPopoverTrigger && mode === 'SELECT_ONLY' && (
                <Box
                  w='100%'
                  h='100%'
                  title={state.showing ? 'Click to select item' : 'Click to open menu'}
                  role='button'
                  tabIndex={0}
                  aria-label={ariaLabel}
                  onKeyPress={target => {
                    if (target.key === 'Enter') {
                      if (!state.showing) {
                        changeVisibility(true)
                      } else {
                        const selected = optionsValues[state.cursor]
                        if (optionsValues.length > 0 && selected) {
                          selectItem(state.cursor)
                        }
                      }
                    }
                  }}
                  onClick={e => {
                    e.stopPropagation()
                    changeVisibility(!state.showing)
                  }}
                >
                  <Field
                    {...others}
                    label={labelStyle === 'hide-on-select' ? '' : label}
                    labelPlacement={labelPlacement}
                    value={value}
                    id={id}
                    h={label ? 11 : 8}
                    isFillBgOnHover={isFillBgOnHover}
                    endContent={
                      (!arrowDownHidden || onRemoveIconClick) && (
                        <Flex>
                          {onRemoveIconClick && (
                            <UnstyledButton
                              onClick={onRemoveIconClick}
                              data-test-id='SelectRemoveButton'
                            >
                              <Icon
                                icon={SvgCrossCircle}
                                pointer
                                svg={{ fill: 'none' }}
                                color={removeIconColor ?? 'icon/error'}
                                mr={2}
                              />
                            </UnstyledButton>
                          )}
                          {!arrowDownHidden && (
                            <Icon color='icon/indicator' icon={SvgArrowTriangleDown} pointer />
                          )}
                        </Flex>
                      )
                    }
                    focused={disableFocusedStyles ? false : state.showing}
                  >
                    {!hideSelectedOption && (
                      <Flex
                        ref={inputRef}
                        role='button'
                        aria-haspopup='listbox'
                        h='100%'
                        w='100%'
                        style={{ outline: 'none' }}
                        data-test-id={`select:listbox:${id}`}
                      >
                        <Flex alignItems='center' w='100%'>
                          {typeof selectedOptionContent === 'string' ? (
                            <Text fontSize={12}>{selectedOptionContent}</Text>
                          ) : (
                            <Flex alignItems='center' w='100%'>
                              {selectedOptionContent}
                              {Array.isArray(value) && value.length > 1 ? (
                                <Text>, +{value.length - 1}</Text>
                              ) : (
                                ''
                              )}
                            </Flex>
                          )}
                        </Flex>
                      </Flex>
                    )}
                  </Field>
                </Box>
              )}
              <input type='hidden' value={value} data-test-id={`select:${id}:${value}`} />
              <input
                type='hidden'
                data-test-id={`select:${id}:enabled`}
                disabled={others.disabled}
              />
            </PopoverTrigger>

            {state.showing && (
              <PopoverTarget
                w='100%'
                maxWidth={maxWidth}
                className={popoverTargetClassName}
                onOutsideClick={() => {
                  changeVisibility(false)
                  extraOnOutsideClick()
                }}
                isPortal={isPortalForTarget}
              >
                {state.showing && (
                  <Search
                    maxHeight={maxHeight}
                    role='listbox'
                    aria-multiselectable={isMultiSelect}
                    tabIndex={-1}
                    aria-labelledby={id}
                    p={0}
                    overflow='hidden'
                  >
                    {isSearchEnabled && (
                      <Flex>
                        <TextField
                          autoComplete={autoComplete}
                          ref={searchFieldRef}
                          id={`select:search:${id}`}
                          data-test-id={`select:search:${id}`}
                          px={2}
                          h={9}
                          fontWeight={500}
                          endContent={
                            <Icon color='icon/indicator' icon={SvgMagnifyingGlass} pointer />
                          }
                          type='search'
                          placeholder={searchPlaceholder || 'Search..'}
                          placeholderColor='text/form-placeholder'
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleSearch(e.target.value)
                          }
                          value={state.searchValue}
                          borderRadius='none'
                        />
                      </Flex>
                    )}
                    {isMultiSelect && isWithClearAll && !state.searchValue && (
                      <LinkButton
                        {...allOptionsContainerStyles}
                        onClick={handleClearAllChange}
                        data-test-id='Select-clear-all'
                        color='text/action-major'
                      >
                        Clear All
                      </LinkButton>
                    )}
                    {isMultiSelect && isWithSelectAll && !state.searchValue && (
                      <UnstyledButton
                        {...allOptionsContainerStyles}
                        onClick={handleSelectAllChange}
                        data-test-id='Select-select-all'
                      >
                        <Box mr={4}>
                          <Checkbox checked={allChecked} noInput onChange={noop} />
                        </Box>
                        <Text color='text/action-major' fontWeight={600}>
                          Select All
                        </Text>
                      </UnstyledButton>
                    )}
                    <SearchListContainer
                      h='100%'
                      overflowY='auto'
                      maxHeight={
                        maxHeight ? `calc(${maxHeight} - ${defaultSearchInputHeight})` : '250px'
                      }
                      as='ul'
                      m={0}
                      p={0}
                      my={2}
                    >
                      {children &&
                        React.Children.map(
                          children,
                          (item: React.ReactElement<OptionProps>, index: number) => (
                            <MappedOption
                              changeHoverState={changeHoverState}
                              changeVisibility={changeVisibility}
                              index={index}
                              item={item}
                              optionsProps={optionsProps}
                              optionsValues={optionsValues}
                              selectItem={selectItem}
                              setState={setState}
                              {...state}
                              {...props}
                            />
                          )
                        )}
                    </SearchListContainer>
                    {bottomPopoverContent}
                  </Search>
                )}
              </PopoverTarget>
            )}
            {state.showCustomContentSection && customContentSection}
          </div>
        </CursorManager>
      </Box>
    </Popover>
  )
}

/**
 * @deprecated - Use Select from '@chilipiper/design-system/src/new' instead if
 *   labelPlacement='outside' or no label and LabelSelect otherwise
 */
export const Select = forwardRef(SelectInner) as <ValueType extends unknown = string>(
  props: SelectProps<ValueType> & { ref?: ComponentRef<typeof SelectInner> }
) => ReturnType<typeof SelectInner>

export const MultiSelect = (props: SelectProps<string[]>) => (
  <Select isMultiSelect={true} {...props} />
)

type FinalSelectProps<ValueType = string> = Omit<SelectProps<ValueType>, 'name' | 'onChange'> & {
  conditionalError?: boolean
  name: string
}

/**
 * @deprecated - Use Select from '@chilipiper/design-system/src/new' instead if
 *   labelPlacement='outside' or no label and LabelSelect otherwise
 */
export const FinalSelectField = <ValueType extends unknown = string>({
  name,
  conditionalError,
  ...props
}: FinalSelectProps<ValueType>) => {
  return (
    <FinalField name={name}>
      {({ input, meta }) => (
        <Select
          {...props}
          {...input}
          error={(conditionalError && meta.error) || (meta.touched && meta.error)}
        />
      )}
    </FinalField>
  )
}
